import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, TextField, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { dispatch, useSelector } from '../../../../redux/store';
import { addGuestBilling } from '../../../../redux/slices/guestRedux/guestBillingRedux';
import { getGuestFromBard } from '../../../../redux/slices/guestRedux/guestRedux';
import { FOOD_CATEGORIES } from '../../../../utils/constants';

const CustomSaleForm = ({ cardId }) => {
  const [formData, setFormData] = useState({ title: '', description: '', qty: 1, cost: 0, price: 0, category: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addData: guestBillingAdded } = useSelector((state) => state.GuestBillings);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const isNumeric = ['qty', 'price', 'cost'].includes(name);
    setFormData((prev) => ({ ...prev, [name]: isNumeric ? (value === '' ? '' : Number(value)) : value }));
  };

  const handleCategoryChange = (event, newValue) => {
    setFormData((prev) => ({ ...prev, category: newValue }));
  };

  useEffect(() => {
    if (guestBillingAdded?.data) {
      dispatch(getGuestFromBard(cardId));
    }
  }, [guestBillingAdded]);

  const validateForm = () => {
    if (!String(formData.title || '').trim()) {
      toast.error('Title is required');
      return false;
    }
    if (formData.qty === '' || Number.isNaN(Number(formData.qty)) || Number(formData.qty) < 1) {
      toast.error('Quantity must be at least 1');
      return false;
    }
    if (formData.cost === '' || Number.isNaN(Number(formData.cost))) {
      toast.error('Cost is required');
      return false;
    }
    if (Number(formData.cost) < 0) {
      toast.error('Cost cannot be negative');
      return false;
    }
    if (formData.price === '' || Number.isNaN(Number(formData.price))) {
      toast.error('Price is required');
      return false;
    }
    if (Number(formData.price) < 0) {
      toast.error('Price cannot be negative');
      return false;
    }
    if (Number(formData.cost) > Number(formData.price)) {
      toast.error('Cost cannot exceed selling price');
      return false;
    }
    if (!formData.category) {
      toast.error('Category is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await dispatch(
        addGuestBilling({
          item: { name: formData.title, description: formData.description, price: Number(formData.price), cost: Number(formData.cost), category: formData.category?.value },
          qty: Number(formData.qty),
          cardId,
        })
      );
      toast.success('Item added to invoice successfully!');
      setFormData({ title: '', description: '', qty: 1, cost: 0, price: 0, category: null });
    } catch (error) {
      toast.error('Failed to add item to invoice. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Add Custom Sale
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField fullWidth label="Title" name="title" value={formData.title} onChange={handleChange} required />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} multiline rows={3} />
        </Grid>
        <Grid item xs={4}>
          <TextField fullWidth label="Quantity" name="qty" type="number" value={formData.qty} onChange={handleChange} inputProps={{ min: 1 }} required />
        </Grid>
        <Grid item xs={4}>
          <TextField fullWidth label="Cost" name="cost" type="number" value={formData.cost} onChange={handleChange} inputProps={{ min: 0, step: '0.01' }} required />
        </Grid>
        <Grid item xs={4}>
          <TextField fullWidth label="Price" name="price" type="number" value={formData.price} onChange={handleChange} inputProps={{ min: 0, step: '0.01' }} required />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            value={formData.category}
            onChange={handleCategoryChange}
            options={FOOD_CATEGORIES}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => <TextField {...params} label="Category" required />}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disableClearable
          />
        </Grid>
        <Grid item xs={12}>
          <LoadingButton type="submit" variant="contained" fullWidth loading={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add to Invoice'}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomSaleForm;
